import {
  sItemIntToKey,
  sItemKeyToInt,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
} from '../../../shared-utilities/models-old/datastructures';
import {INotificationDuplicateBarcodeError} from '../models/notification-duplicate-barcode-error.model';
import {collectStockUpdateFieldKeys} from './notification-stock-update-view-changes.utils';

const BARCODE_INT_KEY: string = sItemKeyToInt.barcode;
const MULTIPLE_BARCODES_INT_KEY: string = sItemKeyToInt.multipleBarcodes;

/**
 * Remap Firestore int-keyed stock update fields to string keys expected by stockUpdate().
 */
function remapStockUpdateFields(
  fields: Record<string, StockItemUpdateFieldValue>,
): Record<string, StockItemUpdateFieldValue> {
  const remapped: Record<string, StockItemUpdateFieldValue> = {};

  Object.keys(fields).forEach((key: string) => {
    const mappedKey: string = sItemIntToKey[key] ?? key;
    remapped[mappedKey] = fields[key];
  });

  return remapped;
}

/**
 * Remove failed barcodes from int-keyed n/o field maps before retrying.
 */
function stripBarcodeField(
  fields: Record<string, StockItemUpdateFieldValue>,
  fieldKey: string,
  failedSet: Set<string>,
): void {
  if (!Object.prototype.hasOwnProperty.call(fields, fieldKey)) {
    return;
  }

  const barcodeValue: string = String(fields[fieldKey] ?? '').trim();
  if (!barcodeValue || failedSet.has(barcodeValue)) {
    delete fields[fieldKey];
  }
}

function stripMultipleBarcodesField(
  fields: Record<string, StockItemUpdateFieldValue>,
  fieldKey: string,
  failedSet: Set<string>,
): void {
  if (!Object.prototype.hasOwnProperty.call(fields, fieldKey)) {
    return;
  }

  const multipleValue: StockItemUpdateFieldValue = fields[fieldKey];
  const filteredBarcodes: string[] = Array.isArray(multipleValue)
    ? multipleValue
      .map((barcode: string | number) => barcode.toString().trim())
      .filter((barcode: string) => barcode && !failedSet.has(barcode))
    : [];

  if (filteredBarcodes.length > 0) {
    fields[fieldKey] = filteredBarcodes;
  } else {
    delete fields[fieldKey];
  }
}

/**
 * Remove failed barcodes from int-keyed or string-keyed n/o field maps before retrying.
 */
function stripFailedBarcodesFromFields(
  fields: Record<string, StockItemUpdateFieldValue>,
  failedBarcodes: string[],
): Record<string, StockItemUpdateFieldValue> {
  const updated: Record<string, StockItemUpdateFieldValue> = {...fields};
  const failedSet: Set<string> = new Set<string>(failedBarcodes);

  stripBarcodeField(updated, BARCODE_INT_KEY, failedSet);
  stripBarcodeField(updated, 'barcode', failedSet);
  stripMultipleBarcodesField(updated, MULTIPLE_BARCODES_INT_KEY, failedSet);
  stripMultipleBarcodesField(updated, 'multipleBarcodes', failedSet);

  return updated;
}

/**
 * Build a stock update retry payload from stored api event data.
 */
export function prepareStockUpdateRetryPayload(
  changes: Record<string, StockItemUpdateObj>,
  duplicateBarcodeErrors: INotificationDuplicateBarcodeError[] = [],
  excludeFailedBarcodes: boolean = false,
): Record<string, StockItemUpdateObj> {
  const failedBarcodesByCode: Map<string, string[]> = new Map<string, string[]>(
    duplicateBarcodeErrors.map((error: INotificationDuplicateBarcodeError) => [error.stockCode, error.failedBarcodes]),
  );
  const payload: Record<string, StockItemUpdateObj> = {};

  Object.keys(changes).forEach((code: string) => {
    const fieldKeys: Set<string> = collectStockUpdateFieldKeys(changes[code]);
    let nextFields: Record<string, StockItemUpdateFieldValue> = {};
    let oldFields: Record<string, StockItemUpdateFieldValue> = {};

    fieldKeys.forEach((key: string) => {
      if (changes[code].n && Object.prototype.hasOwnProperty.call(changes[code].n, key)) {
        nextFields[key] = changes[code].n[key];
      }
      if (changes[code].o && Object.prototype.hasOwnProperty.call(changes[code].o, key)) {
        oldFields[key] = changes[code].o[key];
      }
    });

    if (excludeFailedBarcodes && failedBarcodesByCode.has(code)) {
      const failedBarcodes: string[] = failedBarcodesByCode.get(code) ?? [];
      nextFields = stripFailedBarcodesFromFields(nextFields, failedBarcodes);
      oldFields = stripFailedBarcodesFromFields(oldFields, failedBarcodes);
    }

    const remappedN: Record<string, StockItemUpdateFieldValue> = remapStockUpdateFields(nextFields);
    const remappedO: Record<string, StockItemUpdateFieldValue> = remapStockUpdateFields(oldFields);

    if (Object.keys(remappedN).length === 0) {
      return;
    }

    payload[code] = {
      details: changes[code].details ?? {[1]: ''},
      n: remappedN,
      o: remappedO,
    };
  });

  return payload;
}
